import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import RenderHistoryGrid from './RenderHistoryGrid';
import { FLYTHROUGH_MAX_RENDER_SELECTION } from '../utils/photorealistic/flythroughVideoConfig';

const FlythroughRenderLibraryPickerModal = ({
  open = false,
  items = [],
  loading = false,
  error = null,
  selectedIds = [],
  onClose,
  onConfirm,
}) => {
  const [pendingSelectedIds, setPendingSelectedIds] = useState([]);

  useEffect(() => {
    if (!open) return;
    setPendingSelectedIds(selectedIds.map((id) => String(id)));
  }, [open, selectedIds]);

  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (event) => {
      if (event.key === 'Escape') onClose?.();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, onClose]);

  const handleToggleItem = (item) => {
    const itemId = item?.id;
    if (itemId == null) return;
    const id = String(itemId);
    setPendingSelectedIds((prev) => {
      if (prev.includes(id)) {
        return prev.filter((entryId) => entryId !== id);
      }
      if (prev.length >= FLYTHROUGH_MAX_RENDER_SELECTION) return prev;
      return [...prev, id];
    });
  };

  if (!open) return null;

  const showLoadingState = loading && items.length === 0 && !error;

  const modal = (
    <div
      className="fixed inset-0 z-[120] flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-label="Select renders from library"
      onClick={() => onClose?.()}
    >
      <div
        className="flex max-h-[88vh] w-full max-w-[min(92vw,520px)] flex-col rounded-2xl border border-white/20 bg-white p-4 shadow-2xl"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="mb-3 flex items-start justify-between gap-3">
          <div>
            <h4 className="text-base font-semibold text-gray-800">Render Library</h4>
            <p className="mt-0.5 text-xs text-gray-500">
              Choose up to {FLYTHROUGH_MAX_RENDER_SELECTION} keyframes ({pendingSelectedIds.length}/
              {FLYTHROUGH_MAX_RENDER_SELECTION})
            </p>
          </div>
          <button
            type="button"
            onClick={() => onClose?.()}
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-700 shadow-sm transition-colors hover:border-primary-300 hover:text-primary-300"
            title="Close"
            aria-label="Close"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden
            >
              <path d="M18 6L6 18" />
              <path d="M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto overscroll-y-contain pr-0.5">
          {showLoadingState ? (
            <p className="py-8 text-center text-xs text-gray-500">Loading renders…</p>
          ) : error ? (
            <p className="py-8 text-center text-xs text-red-600">{error}</p>
          ) : items.length === 0 ? (
            <p className="py-8 text-center text-xs text-gray-500">
              No saved renders yet. Save renders to the Render Library first.
            </p>
          ) : (
            <RenderHistoryGrid
              items={items}
              columns={3}
              compareActive
              compareSelectedIds={pendingSelectedIds}
              onCompareToggleItem={handleToggleItem}
              maxCompareSelection={FLYTHROUGH_MAX_RENDER_SELECTION}
            />
          )}
        </div>

        <div className="mt-4 flex shrink-0 gap-2">
          <button
            type="button"
            onClick={() => onClose?.()}
            className="flex-1 rounded-full border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:border-primary-300 hover:text-primary-300"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => onConfirm?.(pendingSelectedIds)}
            disabled={pendingSelectedIds.length === 0}
            className="flex-1 rounded-full border border-gray-800 bg-gray-800 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-gray-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
};

export default FlythroughRenderLibraryPickerModal;
